/**
 * PermissionChecker - Frontend utility for RBAC permission checks.
 *
 * Reads roleData, dataAccessRules, and fieldAccessRules from sessionStorage
 * (set at login time). Provides methods for:
 *   - Operation permissions (canView, canEdit, canDelete, canAdd)
 *   - Field-level access (canViewField, canEditField, getFieldMaskType)
 *   - Column/form filtering (filterColumnsConfig, filterFormFields)
 *   - Data scope awareness (getDataScope)
 *
 * Usage:
 *   import PermissionChecker from "../common/PermissionChecker";
 *   if (PermissionChecker.canDelete("employees")) { ... }
 *   const visibleColumns = PermissionChecker.filterColumnsConfig(columns, "employees");
 */
class PermissionChecker {
  // ── Role Data (operation-level: employees_view, claims_delete, etc.) ──

  static getRoleData() {
    try {
      return JSON.parse(sessionStorage.getItem("roleData") || "{}");
    } catch {
      return {};
    }
  }

  static canPerformOperation(resource, operation) {
    const roleData = this.getRoleData();
    const key = resource + "_" + operation;
    return roleData[key] === true || roleData[key] === "true";
  }

  static canView(resource) {
    return this.canPerformOperation(resource, "view");
  }

  static canAdd(resource) {
    return this.canPerformOperation(resource, "create");
  }

  static canEdit(resource) {
    return this.canPerformOperation(resource, "update");
  }

  static canDelete(resource) {
    return this.canPerformOperation(resource, "delete");
  }

  // ── Data Access Rules (scope-level: what RECORDS a user sees) ──

  static getDataAccessRules() {
    try {
      return JSON.parse(sessionStorage.getItem("dataAccessRules") || "[]");
    } catch {
      return [];
    }
  }

  /**
   * Get the scope rules for a resource + operation.
   * Returns sorted by priority DESC (highest first).
   */
  static getDataScope(resource, operation) {
    const rules = this.getDataAccessRules();
    return rules
      .filter(
        (r) =>
          (r.resource === "*" ||
            r.resource.toLowerCase() === resource.toLowerCase()) &&
          (r.operation === "*" ||
            r.operation.toLowerCase() === operation.toLowerCase())
      )
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }

  /**
   * Get the primary scope type for a resource + operation.
   * Returns "all" if no rules exist.
   */
  static getScopeType(resource, operation) {
    const scopes = this.getDataScope(resource, operation);
    if (scopes.length === 0) return "all";
    return scopes[0].scopeType || "all";
  }

  // ── Field Access Rules (field-level: what COLUMNS a user sees) ──

  static getFieldAccessRules() {
    try {
      return JSON.parse(sessionStorage.getItem("fieldAccessRules") || "[]");
    } catch {
      return [];
    }
  }

  /**
   * Get field rules for a specific resource.
   * Returns rules matching the resource or wildcard "*".
   */
  static getFieldRulesForResource(resource) {
    const rules = this.getFieldAccessRules();
    return rules.filter(
      (r) =>
        r.resource === "*" ||
        r.resource.toLowerCase() === resource.toLowerCase()
    );
  }

  static canViewField(resource, fieldName) {
    const rules = this.getFieldRulesForResource(resource);
    const rule = rules.find((r) => r.fieldName === fieldName);
    if (!rule) return true; // No rule = allow
    return parseInt(rule.canView) === 1;
  }

  static canEditField(resource, fieldName) {
    const rules = this.getFieldRulesForResource(resource);
    const rule = rules.find((r) => r.fieldName === fieldName);
    if (!rule) return true;
    return parseInt(rule.canEdit) === 1;
  }

  static getFieldMaskType(resource, fieldName) {
    const rules = this.getFieldRulesForResource(resource);
    const rule = rules.find((r) => r.fieldName === fieldName);
    if (!rule) return "none";
    return rule.maskType || "none";
  }

  // ── Column / Form Filtering (for widget adapters) ──

  /**
   * Filter columnsConfig array: remove columns the user can't view.
   * columnsConfig = [{ field: "salary", header: "Salary", ... }, ...]
   */
  static filterColumnsConfig(columnsConfig, resource) {
    const rules = this.getFieldRulesForResource(resource);
    if (!rules.length) return columnsConfig;

    return columnsConfig.filter((col) => {
      const rule = rules.find((r) => r.fieldName === col.field);
      if (!rule) return true; // No rule = show
      return parseInt(rule.canView) === 1;
    });
  }

  /**
   * Filter form fields: mark non-editable fields as readOnly, hide non-viewable.
   * formFields = [{ fieldName: "salary", displayName: "Salary", ... }, ...]
   */
  static filterFormFields(formFields, resource) {
    const rules = this.getFieldRulesForResource(resource);
    if (!rules.length) return formFields;

    return formFields
      .map((field) => {
        const rule = rules.find((r) => r.fieldName === field.fieldName);
        if (!rule) return field;
        if (parseInt(rule.canView) === 0) return null; // Hide entirely
        if (parseInt(rule.canEdit) === 0) {
          return { ...field, readOnly: true };
        }
        return field;
      })
      .filter(Boolean);
  }

  /**
   * Check actionsConfig against roleData permissions.
   * Returns a filtered actionsConfig with canAdd/canEdit/canDelete adjusted.
   */
  static filterActionsConfig(actionsConfig, resource) {
    if (!actionsConfig) return actionsConfig;

    return {
      ...actionsConfig,
      canAdd: actionsConfig.canAdd && this.canAdd(resource),
      canEdit: actionsConfig.canEdit && this.canEdit(resource),
      canDelete: actionsConfig.canDelete && this.canDelete(resource),
    };
  }
}

// Legacy exports for backward compatibility
export function hasPermission(permissions, resourceType, resourceId, actionType) {
  if (!permissions || permissions.length === 0) return true;
  const match = permissions.find(
    (p) =>
      p.resourceType === resourceType &&
      parseInt(p.resourceId) === parseInt(resourceId) &&
      p.actionType === actionType
  );
  if (!match) return true;
  return parseInt(match.isAllowed) === 1;
}

export function filterColumns(columns, permissions, widgetInstanceId) {
  if (!permissions || permissions.length === 0) return columns;
  return columns.filter((col) => {
    return hasPermission(permissions, "column", widgetInstanceId, "view");
  });
}

export default PermissionChecker;
